import React, { useRef, useEffect, useState } from 'react';


/**
 * SmartClipPlayer
 * 
 * JIT (Just-in-Time) Player that loops a segment of the source video 
 * and overlays captions without requiring a server-side render.
 * Displays in 9:16 vertical format with play/pause controls.
 */
const SmartClipPlayer = ({
    sourceUrl,
    renderedUrl,  // URL of pre-rendered clip (from smart crop/rerender)
    start,
    end,
    captions = [],
    isPlaying: externalIsPlaying,
    onTimeUpdate,
    captionStyle = 'default',
    captionSize = 90,
    captionVerticalPosition = 15,  // Bottom position as percentage (default 15%)
    includeCaptions = true
}) => {
    const videoRef = useRef(null);
    const requestRef = useRef();
    const [currentTime, setCurrentTime] = useState(start);
    const [videoError, setVideoError] = useState(null);
    const [internalPlaying, setInternalPlaying] = useState(false);
    const [isHovering, setIsHovering] = useState(false);

    const [isMuted, setIsMuted] = useState(false); // Default unmuted

    // Use external control if provided, otherwise use internal state
    const isPlaying = externalIsPlaying !== undefined ? externalIsPlaying : internalPlaying;

    // Reset error state when video source changes
    useEffect(() => {
        setVideoError(null);
    }, [sourceUrl, renderedUrl]);

    // Sync video position with start prop
    useEffect(() => {
        if (videoRef.current) {
            if (Math.abs(videoRef.current.currentTime - start) > 0.5) {
                videoRef.current.currentTime = start;
            }
        }
    }, [start]);

    // Handle Play/Pause
    useEffect(() => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.play().catch(e => {
                    console.warn("Autoplay prevented:", e);
                    // If autoplay fails, try muted
                    videoRef.current.muted = true;
                    setIsMuted(true);
                    videoRef.current.play().catch(e2 => {
                        console.warn("Muted autoplay also failed:", e2);
                    });
                });
            } else {
                videoRef.current.pause();
            }
        }
    }, [isPlaying]);

    // High-precision timing check (only for JIT mode)
    const checkLoop = () => {
        if (videoRef.current) {
            const t = videoRef.current.currentTime;
            setCurrentTime(t);
            if (onTimeUpdate) onTimeUpdate(t);

            // Stop at end of segment if NOT playing a rendered clip
            if (!renderedUrl && t >= end) {
                videoRef.current.pause();
                videoRef.current.currentTime = start;
                setInternalPlaying(false);
            }
        }
        requestRef.current = requestAnimationFrame(checkLoop);
    };

    useEffect(() => {
        requestRef.current = requestAnimationFrame(checkLoop);
        return () => cancelAnimationFrame(requestRef.current);
    }, [start, end]);

    // Calculate current caption - returns array of word objects
    const getCurrentCaption = () => {
        if (!includeCaptions || !captions || captions.length === 0) return null;

        // Find all words currently active (within a window around currentTime)
        // Show ~3-4 words at a time for readability
        // Show ~3-4 words at a time for readability
        const WORDS_PER_PAGE = 4;

        // Find the current word index
        let currentIdx = captions.findIndex(w =>
            currentTime >= w.start && currentTime <= w.end
        );

        if (currentIdx === -1) {
            // Not exactly on a word, find the closest upcoming or previous word
            currentIdx = captions.findIndex(w => w.start > currentTime);
            if (currentIdx > 0) currentIdx--; // Go back to previous word
            if (currentIdx === -1) currentIdx = captions.length - 1; // At end
        }

        // Get a window of words around current position, but static (paged)
        // Instead of sliding window [i, i+3], use pages [0,3], [4,7], etc.
        const startIdx = Math.floor(Math.max(0, currentIdx) / WORDS_PER_PAGE) * WORDS_PER_PAGE;
        const endIdx = Math.min(captions.length, startIdx + WORDS_PER_PAGE);

        // Build array of word objects with 'active' flag
        const words = captions.slice(startIdx, endIdx).map((w, idx) => {
            const absoluteIdx = startIdx + idx;
            // A word is "active" if it's the current one being spoken
            // OR for karaoke style, if it's already been spoken in this phrase
            const isActive = absoluteIdx === currentIdx;
            const isPast = absoluteIdx < currentIdx;

            return {
                text: w.word || w.punctuated_word || '',
                active: isActive,
                past: isPast
            };
        });

        return words.length > 0 ? words : null;
    };

    // Style mapping for captions - matches CAPTION_PRESETS
    // Returns base style + function to get color for a specific word
    const getCaptionStyle = () => {
        const base = {
            position: 'absolute',
            bottom: `${captionVerticalPosition}%`,
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            pointerEvents: 'none',
            zIndex: 10,
            fontSize: `${Math.max(14, captionSize * 0.22)}px`, // Reduced multiplier
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.9)',
            transition: 'all 0.1s ease',
            width: '90%',
            wordBreak: 'break-word',
            lineHeight: 1.3,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '0.25em'
        };

        const getWordStyle = (word, styleId) => {
            const isHighlight = word.active;

            // Common styles
            const wordBase = {
                transition: 'transform 0.1s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.1s', // Bouncy spring
                display: 'inline-block',
                transform: isHighlight ? 'scale(1.15) rotate(-1deg)' : 'scale(1)', // Slight rotation adds dynamic feel
            };

            switch (styleId) {
                case 'karaoke':
                    return {
                        ...wordBase,
                        color: (word.active || word.past) ? '#fff' : 'rgba(255,255,255,0.5)',
                        fontFamily: 'Impact, sans-serif',
                        textTransform: 'uppercase',
                        WebkitTextStroke: '1px #000',
                    };
                case 'beasty':
                    return {
                        ...wordBase,
                        color: isHighlight ? '#39ff14' : '#fff', // Green highlight
                        fontFamily: 'Impact, sans-serif',
                        textTransform: 'uppercase',
                        textShadow: '3px 3px 0 #000, -1px -1px 0 #000',
                    };
                case 'deep_diver':
                    // Deep diver is usually a box, so per-word styling is tricky if it's one bg
                    // For now, let's keep all same color or make active blacker?
                    // Actually deep diver usually has white text on semi-transparent bg
                    return {
                        ...wordBase,
                        color: '#000',
                        fontWeight: '900',
                    };
                case 'pod_p':
                    return {
                        ...wordBase,
                        color: isHighlight ? '#ff69b4' : '#fff', // Pink highlight
                        fontFamily: 'Montserrat, sans-serif',
                        textTransform: 'uppercase',
                        textShadow: '2px 2px 0 #000',
                    };
                case 'youshaei':
                    return {
                        ...wordBase,
                        color: isHighlight ? '#00ffff' : '#fff', // Cyan highlight
                        fontFamily: 'Arial Black, sans-serif',
                        textTransform: 'uppercase',
                        textShadow: '2px 2px 0 #000',
                    };
                case 'mozi':
                    return {
                        ...wordBase,
                        color: isHighlight ? '#ffea00' : '#fff', // Yellow highlight
                        fontFamily: 'Arial, sans-serif',
                        fontWeight: '900',
                        textTransform: 'uppercase',
                        textShadow: '2px 2px 0 #000',
                    };
                case 'default':
                default:
                    return {
                        ...wordBase,
                        color: isHighlight ? '#ffff00' : 'white', // Basic yellow highlight
                        fontFamily: 'Inter, Arial, sans-serif'
                    };
            }
        };

        // Container styles specific to presets
        let containerStyle = { ...base };
        if (captionStyle === 'deep_diver') {
            containerStyle.backgroundColor = 'rgba(255,255,255,0.95)';
            containerStyle.padding = '6px 12px';
            containerStyle.borderRadius = '4px';
            containerStyle.textShadow = 'none';
        }

        return { containerStyle, getWordStyle };
    };

    if (!sourceUrl) {
        return <div className="smart-player-error" style={{
            aspectRatio: '9/16',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#1a1a1a',
            color: '#666',
            fontSize: '12px'
        }}>No source video available</div>;
    }

    return (
        <div
            className="smart-clip-player-container"
            style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '9/16',
                backgroundColor: '#000',
                overflow: 'hidden',
                borderRadius: '8px',
            }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <video
                ref={videoRef}
                src={renderedUrl || sourceUrl}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover' // Cover to fill 9:16 container
                }}
                playsInline
                controls // Enable native controls
                muted={isMuted} // Controlled by state, but user can change via controls
                onVolumeChange={(e) => setIsMuted(e.target.muted)}
                onPlay={() => setInternalPlaying(true)}
                onPause={() => setInternalPlaying(false)}
                onLoadedMetadata={() => {
                    if (videoRef.current) {
                        videoRef.current.currentTime = start;
                    }
                }}
                onError={(e) => {
                    console.error('[SmartClipPlayer] Video error:', e.target?.error?.message || 'Unknown error');
                    setVideoError("Failed to load video");
                }}
            />

            {/* Caption Overlay */}
            {includeCaptions && (
                <div className="caption-layer" style={getCaptionStyle().containerStyle}>
                    {getCurrentCaption()?.map((wordObj, i) => (
                        <span key={i} style={getCaptionStyle().getWordStyle(wordObj, captionStyle)}>
                            {wordObj.text}
                        </span>
                    ))}
                </div>
            )}

            {/* Error Overlay */}
            {videoError && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.8)',
                    color: '#999',
                    fontSize: '12px',
                    padding: '16px',
                    textAlign: 'center'
                }}>
                    {videoError}
                </div>
            )}
        </div>
    );
};

export default SmartClipPlayer;
